import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import ReactSelect from 'dashboard/components/ReactSelect';
import { CaseAssignmentPolicyAttributes } from 'dashboard/models/case-assignment-policy';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

const DISTRIBUTION_TYPE_OPTIONS: {
  label: string;
  value: CaseAssignmentPolicyAttributes['distribution-type'];
}[] = [
  {
    label: 'Round robin',
    value: 'round_robin',
  },
  {
    label: 'Case creator',
    value: 'case_creator',
  },
  {
    label: 'Inquiry creator',
    value: 'inquiry_creator',
  },
  {
    label: 'Fewest cases',
    value: 'fewest_cases',
  },
];

const ORDER_BY_OPTIONS: {
  label: string;
  value: CaseAssignmentPolicyAttributes['assignment-order-by'];
}[] = [
  {
    label: 'Created at',
    value: 'created_at',
  },
  {
    label: 'SLA expiration',
    value: 'sla_expires_at',
  },
];

interface FormCreateCaseAssignmentPolicyProps {
  defaultValues: Partial<CreateCaseAssignmentPolicyFormData>;
  submit: (formData: CreateCaseAssignmentPolicyFormData) => void;
  handleCancel: () => void;
}

const FormCreateCaseAssignmentPolicy = ({
  defaultValues,
  handleCancel,
  submit,
}: FormCreateCaseAssignmentPolicyProps) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors, isValid },
  } = useFormSecurely<CreateCaseAssignmentPolicyFormData>({
    defaultValues,
    mode: 'onChange',
  });

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Label size='md' htmlFor='form-create-case-assignment-policy__name' weight='bold'>
        Policy name
      </Label>
      <Spacer height={1} />
      <Input
        {...register('name', {
          required: 'Please give your assignment policy a name',
          minLength: {
            value: 2,
            message: 'Assignment policy names must be at least 2 letters long',
          },
        })}
        id='form-create-case-assignment-policy__name'
        placeholder='Name'
        type='text'
      />
      {errors.name && <FormError>{errors.name.message}</FormError>}
      <Spacer height={3} />

      <Label
        htmlFor='form-create-case-assignment-policy__distribution'
        id='form-create-case-assignment-policy__distribution--label'
        size='md'
        weight='bold'
      >
        Distribution
      </Label>
      <Spacer height={1} />
      <Controller
        control={control}
        name='distributionType'
        rules={{
          required: 'Please select a distribution type',
        }}
        render={({ field: { onChange, value } }) => (
          <ReactSelect
            id='form-create-case-assignment-policy__distribution-type'
            aria-labelledby='form-create-case-assignment-policy__distribution--label'
            options={DISTRIBUTION_TYPE_OPTIONS}
            value={value}
            onChange={onChange}
            isSearchable
            portal
          />
        )}
      />
      {errors.distributionType && <FormError>{errors.distributionType.message}</FormError>}
      <Spacer height={3} />

      <Label
        htmlFor='form-create-case-assignment-policy__order-by'
        id='form-create-case-assignment-policy__order-by--label'
        size='md'
        weight='bold'
      >
        Case sort order
      </Label>
      <Spacer height={1} />
      <Controller
        control={control}
        name='assignmentOrderBy'
        rules={{
          required: 'Please select an assignment order',
        }}
        render={({ field: { onChange, value } }) => (
          <ReactSelect
            id='form-create-case-assignment-policy__order-by-type'
            aria-labelledby='form-create-case-assignment-policy__order-by--label'
            options={ORDER_BY_OPTIONS}
            value={value}
            onChange={onChange}
            isSearchable
            portal
          />
        )}
      />
      {errors.assignmentOrderBy && <FormError>{errors.assignmentOrderBy.message}</FormError>}
      <Spacer height={3} />

      <HStack equal={true}>
        <Button
          variant='basic'
          id='form-create-case-assignment-policy__cancel'
          data-test='form-create-case-assignment-policy__cancel'
          disabled={isSubmitting}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='form-create-case-assignment-policy__submit'
          disabled={isSubmitting || !isValid}
          isPending={isSubmitting}
        >
          Create
        </Button>
      </HStack>
    </form>
  );
};

export interface CreateCaseAssignmentPolicyFormData {
  name: string;
  distributionType: CaseAssignmentPolicyAttributes['distribution-type'];
  assignmentOrderBy: CaseAssignmentPolicyAttributes['assignment-order-by'];
}

export default FormCreateCaseAssignmentPolicy;
